// @ts-check
import { EyeOutlined, SearchOutlined, SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Empty,
  Grid,
  Input,
  List,
  PageHeader,
  Select,
  Skeleton,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { useMemo } from "react";
import { BiWind } from "react-icons/bi";
import { FaCloudRain, FaSun } from "react-icons/fa";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DeviceProvider, deviceImageMap, useDevice } from "../contexts/device-context";
import tr from "../i18n";
import { hasAccess, ROLES } from "../utils/AccessLevels";
import { AccessGate } from "../components/AccessGate";

const TR_FOLD = { ç: "c", ğ: "g", ı: "i", İ: "i", ö: "o", ş: "s", ü: "u", I: "i" };
const normalizeTr = (value) =>
  String(value ?? "").replace(/[çğıİöşüI]/g, (c) => TR_FOLD[c]).toLowerCase();

/** Accepts an array, a comma string, or null and always returns a clean array. */
const toArray = (value) =>
  Array.isArray(value)
    ? value.filter(Boolean)
    : String(value ?? "")
        .split(",")
        .filter(Boolean);

const MODEL_OPTIONS = [
  { text: "BLT", value: "BL" },
  { text: "Farmer", value: "ELS" },
  { text: "Gardener", value: "EL" },
  { text: "SimPro", value: "LT" },
  { text: "NemPro", value: "NEM" },
];

const SORT_OPTIONS = [
  { field: "name", label: "Device Name" },
  { field: "model", label: "Device Model" },
  { field: "valveCount", label: "Valves" },
  { field: "alive", label: "Status" },
  { field: "battery", label: "Battery" },
  { field: "signal", label: "Signal" },
];

/**
 * Optimization icons, shared between the table row and the mobile card.
 * @param {{ optimization?: import("../types/global").Device["optimization"] }} props
 */
function OptimizationIcons({ optimization }) {
  const { sun, rain, wind, active } = optimization || {};
  return (
    <Space size={8}>
      <Tooltip title={tr("sun")}>
        <FaSun size={18} style={{ color: sun && active ? "orange" : "GrayText" }} />
      </Tooltip>
      <Tooltip title={tr("rain")}>
        <FaCloudRain size={18} style={{ color: rain && active ? "lightskyblue" : "GrayText" }} />
      </Tooltip>
      <Tooltip title={tr("wind")}>
        <BiWind size={18} style={{ color: wind && active ? "blueviolet" : "GrayText" }} />
      </Tooltip>
    </Space>
  );
}

/**
 * One device rendered as a card. Used instead of a table row below the `md` breakpoint.
 * @param {{
 *   record: import("../types/global").Device,
 *   onOpenDetails: (record: any) => void,
 *   onOpenGarden: (gardenId: string) => void,
 * }} props
 */
function DeviceCard({ record, onOpenDetails, onOpenGarden }) {
  const name = record.Device?.name || record.name || "-";
  const valveCount = record?.valveCount || 0;

  return (
    <Card
      size="small"
      bordered
      style={{ marginBottom: 12 }}
      bodyStyle={{ padding: 12 }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <img
          src={deviceImageMap[record.model]}
          alt={record.model}
          style={{ height: 36, flex: "0 0 auto" }}
        />

        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography.Text strong ellipsis style={{ display: "block" }}>
            {name}
          </Typography.Text>

          {record.gardenId ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto", maxWidth: "100%" }}
              onClick={() => onOpenGarden(record.gardenId)}
            >
              <span
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record.gardenName}
              </span>
            </Button>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tr("Unassigned")}
            </Typography.Text>
          )}
        </div>

        <AccessGate role={ROLES.END_USER}>
          <Button
            icon={<EyeOutlined />}
            onClick={() => onOpenDetails(record)}
            shape="circle"
            style={{ flex: "0 0 auto" }}
          />
        </AccessGate>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "8px 16px",
          marginTop: 12,
        }}
      >
        <DeviceProvider.DeviceStatus device={record} />
        <DeviceProvider.DeviceBattery device={record} />
        <DeviceProvider.DeviceSignal device={record} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {valveCount === 1 ? `${valveCount} ${tr("Valve")}` : `${valveCount} ${tr("Valve(s)")}`}
        </Typography.Text>
        <OptimizationIcons optimization={record.optimization} />
      </div>
    </Card>
  );
}

export function Devices2() {
  const {
    devices,
    isFetchingDevicesResponse,
    totalDevices,
    queryState,
    updateQueryParams,
    gardenOptions,
  } = useDevice();
  const { page, limit, searchTerm, model, alive, gardenIds, sortField, sortOrder } = queryState;
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const screens = Grid.useBreakpoint();
  // `screens` is empty on the very first render, so treat "not yet md" as desktop
  // only once we know the width. Defaulting to desktop avoids a card→table flash.
  const isMobile = screens.md === false;

  const openDetails = (record) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("device_id", record.id);
      newParams.set("show_map", "true");
      if (record.waterMeter?.connectedDevices?.length) {
        newParams.set("show_smart_watering", "true");
      }
      return newParams;
    });
  };

  const openGarden = (gardenId) => navigate(`/control-center/gardens/${gardenId}`);

  /**
   * @param {import("antd/lib/table").TablePaginationConfig} pagination
   * @param {Record<string, import("antd/lib/table/interface").FilterValue | null>} filters
   * @param {import("antd/lib/table/interface").SorterResult<import("../types/global").Device> | import("antd/lib/table/interface").SorterResult<import("../types/global").Device>[]} sorter
   */
  const handleChange = (pagination, filters, sorter, extra) => {
    const rawField = Array.isArray(sorter?.field) ? sorter.field.join(".") : sorter?.field;
    // gardenName sorts locally (function sorter); the backend has no such field.
    const isServerSort = !!sorter?.order && rawField !== "gardenName";
    const isPaginating = extra?.action === "paginate";

    updateQueryParams({
      page: isPaginating ? pagination.current : 1,
      limit: pagination.pageSize,
      model: filters.model || [],
      alive: filters.alive?.[0] || null,
      gardenIds: filters.gardenName || [],
      sortField: isServerSort ? rawField : undefined,
      sortOrder: isServerSort ? sorter.order : undefined,
    });
  };

  const gardenFilters = useMemo(
    () => [
      { text: tr("Unassigned"), value: "__none__" },
      ...gardenOptions.map((g) => ({ text: g.name, value: g.id })),
    ],
    [gardenOptions]
  );

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={tr("Search")}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            updateQueryParams({ search: selectedKeys[0] || undefined, page: 1 });
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => {
              confirm();
              updateQueryParams({ search: selectedKeys[0] || undefined, page: 1 });
            }}
          >
            {tr("Search")}
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              updateQueryParams({ search: undefined, page: 1 });
            }}
          >
            {tr("Reset")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ fontSize: 18, color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          document.querySelector(".ant-table-filter-dropdown input")?.focus();
        }, 100);
      }
    },
    // IMPORTANT: controlled from backend state
    filteredValue: searchTerm ? [searchTerm] : null,
  });

  /** @type {import("antd/lib/table").ColumnsType<import("../types/global").Device>} */
  const columns = [
    {
      title: tr("Device Model"),
      dataIndex: "model",
      key: "model",
      width: 130,
      filters: MODEL_OPTIONS,
      filteredValue: toArray(model),
      sorter: true,
      render: (model) => <img src={deviceImageMap[model]} alt={model} style={{ height: 32 }} />,
    },
    {
      title: tr("Device Name"),
      dataIndex: "name",
      key: "name",
      // Grow with the viewport instead of pinning 350px on a 375px-wide phone.
      width: 220,
      ellipsis: true,
      sorter: true,
      ...getColumnSearchProps("name"),
      render: (_, record) => record.Device?.name || record.name || "-",
    },
    {
      title: tr("Garden Name"),
      dataIndex: "gardenName",
      key: "gardenName",
      width: 220,
      ellipsis: true,
      responsive: ["lg"],
      filters: gardenFilters,
      filterSearch: (input, record) => normalizeTr(record.text).includes(normalizeTr(input)),
      filteredValue: toArray(gardenIds),
      sorter: (a, b) => (a.gardenId || "").localeCompare(b.gardenId || "", "tr"),
      render: (_, record) =>
        record.gardenId ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => openGarden(record.gardenId)}
          >
            {record.gardenName}
          </Button>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: tr("Valves"),
      dataIndex: "valveCount",
      key: "valveCount",
      width: 110,
      responsive: ["xl"],
      sorter: true,
      render: (_, record) => {
        const valveCount = record?.valveCount || 0;
        return valveCount === 1
          ? `${valveCount} ${tr("Valve")}`
          : `${valveCount} ${tr("Valve(s)")}`;
      },
    },
    {
      title: tr("Status"),
      dataIndex: "alive",
      key: "alive",
      width: 120,
      filters: [
        { text: tr("Online"), value: "alive" },
        { text: tr("Offline"), value: "dead" },
      ],
      filteredValue: alive ? [alive] : [],
      sorter: true,
      render: (_, record) => <DeviceProvider.DeviceStatus device={record} />,
    },
    {
      title: tr("Battery"),
      dataIndex: "battery",
      key: "battery",
      width: 110,
      responsive: ["lg"],
      sorter: true,
      render: (_, record) => <DeviceProvider.DeviceBattery device={record} />,
    },
    {
      title: tr("Signal"),
      dataIndex: "signal",
      key: "signal",
      width: 110,
      responsive: ["lg"],
      sorter: true,
      render: (_, record) => <DeviceProvider.DeviceSignal device={record} />,
    },
    {
      title: tr("Optimization"),
      key: "optimisations",
      width: 130,
      responsive: ["xl"],
      sorter: false,
      render: (_, record) => <OptimizationIcons optimization={record.optimization} />,
    },
    {
      title: "",
      key: "details",
      width: 64,
      fixed: "right",
      render: (_, record) => (
        <AccessGate role={ROLES.END_USER}>
          <Button icon={<EyeOutlined />} onClick={() => openDetails(record)} size="small" shape="circle" />
        </AccessGate>
      ),
    },
  ];

  if (process.env.NODE_ENV === "development") {
    // @ts-ignore
    window.isFetchingDevicesResponse = isFetchingDevicesResponse;
  }

  /**
   * Filters live in the column headers on desktop. Cards have no headers, so the
   * same controls are surfaced as a toolbar above the list.
   */
  const mobileToolbar = (
    <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 12 }}>
      <Input.Search
        allowClear
        placeholder={tr("Search")}
        defaultValue={searchTerm}
        onSearch={(value) => updateQueryParams({ search: value || undefined, page: 1 })}
      />

      <Space.Compact block>
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          style={{ width: "50%" }}
          placeholder={tr("Device Model")}
          value={toArray(model)}
          onChange={(value) => updateQueryParams({ model: value, page: 1 })}
          options={MODEL_OPTIONS.map((m) => ({ label: m.text, value: m.value }))}
        />
        <Select
          allowClear
          style={{ width: "50%" }}
          placeholder={tr("Status")}
          value={alive || undefined}
          onChange={(value) => updateQueryParams({ alive: value || null, page: 1 })}
          options={[
            { label: tr("Online"), value: "alive" },
            { label: tr("Offline"), value: "dead" },
          ]}
        />
      </Space.Compact>

      <Select
        mode="multiple"
        allowClear
        showSearch
        maxTagCount="responsive"
        style={{ width: "100%" }}
        placeholder={tr("Garden Name")}
        value={toArray(gardenIds)}
        onChange={(value) => updateQueryParams({ gardenIds: value, page: 1 })}
        filterOption={(input, option) => normalizeTr(option?.label).includes(normalizeTr(input))}
        options={gardenFilters.map((g) => ({ label: g.text, value: g.value }))}
      />

      <Space.Compact block>
        <Select
          allowClear
          style={{ width: "100%" }}
          placeholder={tr("Sort by")}
          value={sortField || undefined}
          onChange={(value) =>
            updateQueryParams({
              sortField: value || undefined,
              sortOrder: value ? sortOrder || "ascend" : undefined,
              page: 1,
            })
          }
          options={SORT_OPTIONS.map((s) => ({ label: tr(s.label), value: s.field }))}
        />
        <Button
          disabled={!sortField}
          icon={sortOrder === "descend" ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          onClick={() =>
            updateQueryParams({
              sortOrder: sortOrder === "descend" ? "ascend" : "descend",
              page: 1,
            })
          }
        />
      </Space.Compact>
    </Space>
  );

  return (
    <PageHeader
      ghost={false}
      title={tr("Devices")}
      style={{ padding: isMobile ? "12px 8px" : undefined }}
    >
      <Skeleton loading={isFetchingDevicesResponse} active>
        {isMobile ? (
          <>
            {mobileToolbar}
            <List
              dataSource={devices}
              loading={isFetchingDevicesResponse}
              rowKey={(record) => record._id}
              locale={{ emptyText: <Empty description={tr("No devices to show yet.")} /> }}
              pagination={{
                current: page,
                pageSize: limit,
                total: totalDevices,
                simple: true,
                showSizeChanger: false,
                onChange: (nextPage, nextPageSize) =>
                  updateQueryParams({ page: nextPage, limit: nextPageSize }),
                style: { textAlign: "center" },
              }}
              renderItem={(record) => (
                <DeviceCard
                  key={record._id}
                  record={record}
                  onOpenDetails={openDetails}
                  onOpenGarden={openGarden}
                />
              )}
            />
          </>
        ) : (
          <Table
            columns={columns}
            dataSource={devices}
            rowKey="_id"
            size={screens.xl ? "middle" : "small"}
            loading={isFetchingDevicesResponse}
            onChange={handleChange}
            // Horizontal scroll instead of squashed columns on tablets.
            scroll={{ x: "max-content" }}
            pagination={{
              current: page,
              pageSize: limit,
              total: totalDevices,
              showSizeChanger: true,
            }}
            locale={{
              filterConfirm: tr("OK"),
              filterReset: tr("Reset"),
            }}
          />
        )}
      </Skeleton>
    </PageHeader>
  );
}
